import { type ChangeEvent, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import {
  type BenchmarkAssetType,
  type UploadedAssessmentBenchmarkAsset,
  uploadAssessmentBenchmarkAssetRequest,
} from "../../services/assessmentContentService";

type UploadResult = UploadedAssessmentBenchmarkAsset & {
  fileName: string;
};

type BenchmarkAssetUploadDialogProps = {
  open: boolean;
  apiUrl: string | null | undefined;
  token: string | null;
  benchmarkId: string;
  onClose: () => void;
  onUploadComplete?: () => void;
  isDemo: boolean;
};

export function BenchmarkAssetUploadDialog({
  open,
  apiUrl,
  token,
  benchmarkId,
  onClose,
  onUploadComplete,
  isDemo,
}: BenchmarkAssetUploadDialogProps) {
  const [assetType, setAssetType] = useState<BenchmarkAssetType>("images");
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);

  const normalizedBenchmarkId = benchmarkId.trim();
  const accept = assetType === "images" ? "image/*" : "audio/*";
  const uploadProgress =
    files.length > 0
      ? Math.round((uploadResults.length / files.length) * 100)
      : 0;

  const handleFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFiles(Array.from(event.target.files ?? []));
    setUploadError(null);
    setUploadResults([]);
  };

  const handleAssetTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setAssetType(event.target.value as BenchmarkAssetType);
    setFiles([]);
    setUploadError(null);
    setUploadResults([]);
  };

  const handleClose = () => {
    setFiles([]);
    setUploadError(null);
    setUploadResults([]);
    onClose();
  };

  const handleUpload = async () => {
    if (!normalizedBenchmarkId) {
      setUploadError("Select a benchmark before uploading.");
      return;
    }
    if (files.length === 0) {
      setUploadError("Choose at least one file to upload.");
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadResults([]);
    try {
      const uploaded: UploadResult[] = [];
      for (const file of files) {
        const result = await uploadAssessmentBenchmarkAssetRequest({
          apiUrl,
          token,
          benchmarkId: normalizedBenchmarkId,
          assetType,
          file,
          isDemo,
        });
        uploaded.push({ ...result, fileName: file.name });
        setUploadResults([...uploaded]);
      }
      onUploadComplete?.();
    } catch (error) {
      setUploadError(
        error instanceof Error ? error.message : "Unable to upload files.",
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isUploading ? undefined : handleClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Upload to S3</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FormControl>
            <RadioGroup row value={assetType} onChange={handleAssetTypeChange}>
              <FormControlLabel
                value="images"
                control={<Radio />}
                label="Images"
                disabled={isUploading}
              />
              <FormControlLabel
                value="audio"
                control={<Radio />}
                label="Audio"
                disabled={isUploading}
              />
            </RadioGroup>
          </FormControl>

          <Button variant="outlined" component="label" disabled={isUploading}>
            Choose files
            <input
              hidden
              multiple
              type="file"
              accept={accept}
              onChange={handleFilesChange}
            />
          </Button>

          {files.length > 0 ? (
            <Typography variant="body2" color="text.secondary">
              {files.length} file{files.length === 1 ? "" : "s"} selected
            </Typography>
          ) : null}

          {(isUploading || uploadResults.length > 0) && files.length > 0 ? (
            <Stack spacing={0.75}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Upload progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {uploadResults.length}/{files.length}
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Stack>
          ) : null}

          {uploadError ? <Alert severity="error">{uploadError}</Alert> : null}

          {uploadResults.length === files.length && uploadResults.length > 0 ? (
            <Alert severity="success">
              <Typography variant="body2" fontWeight={600}>
                successfully uploaded all {uploadResults.length} files
              </Typography>
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isUploading}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={isUploading || files.length === 0 || !normalizedBenchmarkId}
        >
          {isUploading ? "Uploading..." : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
